import * as React from 'react';
import { List, CellMeasurer, CellMeasurerCache, ListRowProps } from 'react-virtualized';
import { style } from 'typestyle';

export interface VirtualizedListboxProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * Optional props to control width&height if needed
   */
  listWidth?: number;
  /**
   * The maximum height for the listbox.
   * If the total estimated height is less than this, the listbox will shrink to fit its content.
   * Otherwise, it will be capped at this height and scroll.
   */
  maxListHeight?: number;
  /**
   * Whether to automatically highlight the first option when opened
   */
  autoHighlight?: boolean;
  /**
   * Index of the currently highlighted item
   */
  highlightedIndex?: number;
}

/**
 * Create a CellMeasurerCache allowing variable row heights.
 * The fallback row height is 36, but it will expand if text wraps.
 */
const cache = new CellMeasurerCache({
  defaultHeight: 36,
  fixedWidth: true,
});
const listWrapper = style({
  $nest: {
    '& .ReactVirtualized__Grid__innerScrollContainer': {
      maxWidth: 'none !important',
    },
  },
});
export const VirtualizedListbox: React.FC<VirtualizedListboxProps> = (props) => {
  const {
    onMouseDown,
    onKeyDown,
    listWidth = 200,
    maxListHeight = 400,
    children,
    style: customStyle,
    autoHighlight = true,
    highlightedIndex = 0,
  } = props;

  // Turn children into an array of items.
  const items = React.Children.toArray(children);
  const itemCount = items.length;

  // Estimate the total height by summing up (or approximating) each row’s height.
  // Since the cache might not have measured all rows yet, we use the default for any unknown.
  let totalEstimatedHeight = 0;
  for (let i = 0; i < itemCount; i++) {
    // Try to get the measured row height; if missing, fallback to cache.defaultHeight.
    const rowHeight = cache.rowHeight({ index: i }) || cache.defaultHeight;
    totalEstimatedHeight += rowHeight;
  }
  // The List's height will be the smaller of the total estimated height and the maxListHeight.
  const computedHeight = Math.min(totalEstimatedHeight, maxListHeight);

  const containerStyle: React.CSSProperties = {
    width: '100%',
    height: computedHeight,
    overflowX: 'hidden',
    overflowY: 'auto',
    ...customStyle,
  };

  // A ref to the List so we can force it to recompute heights when children change.
  const listRef = React.useRef<List>(null);

  // Whenever items change, clear the cache and re-measure.
  React.useEffect(() => {
    cache.clearAll();
    listRef.current?.recomputeRowHeights();
  }, [children, itemCount]);

  // Scroll to the highlighted index when the component mounts or highlighted index changes
  React.useEffect(() => {
    if (autoHighlight && highlightedIndex === 0 && itemCount > 0 && listRef.current) {
      // Scroll to the first item
      listRef.current.scrollToRow(0);
    } else if (highlightedIndex !== null && highlightedIndex >= 0 && listRef.current) {
      // Scroll to the highlighted item
      listRef.current.scrollToRow(highlightedIndex);
    }
  }, [autoHighlight, highlightedIndex, itemCount]);

  // Row renderer: wrap each row in a CellMeasurer so its height can adjust.
  function rowRenderer({ index, key, style, parent }: ListRowProps) {
    return (
      <CellMeasurer key={key} cache={cache} parent={parent} columnIndex={0} rowIndex={index}>
        {() => {
          // Check if this is the highlighted item
          const isHighlighted = autoHighlight && index === highlightedIndex;

          // Merge provided row styles with any custom styles.
          const finalRowStyle: React.CSSProperties = {
            ...style,
            whiteSpace: 'normal',
            boxSizing: 'border-box',
            listStyle: 'none',
            backgroundColor: isHighlighted ? 'rgba(0, 168, 155, 0.45)' : undefined,
          };
          // Set aria-selected for accessibility
          return (
            <div style={finalRowStyle} aria-selected={isHighlighted}>
              {items[index]}
            </div>
          );
        }}
      </CellMeasurer>
    );
  }

  return (
    <div onMouseDown={onMouseDown} onKeyDown={onKeyDown} role="listbox" style={containerStyle}>
      <List
        ref={listRef}
        autoWidth
        width={listWidth}
        height={computedHeight}
        rowCount={itemCount}
        deferredMeasurementCache={cache}
        rowHeight={cache.rowHeight}
        rowRenderer={rowRenderer}
        overscanCount={5}
        style={{ outline: 'none' }}
        className={listWrapper}
        scrollToIndex={autoHighlight ? 0 : undefined}
      />
    </div>
  );
};

export default VirtualizedListbox;
